/**
 * Buy X, Get Y promotion messaging.
 *
 * Explains progress toward a Shopify *automatic* Buy X Get Y discount. It never
 * calculates, applies, or claims a discount, and it never adds anything to the
 * cart — the customer chooses the qualifying items and Shopify does the pricing.
 *
 * One file backs every placement (standalone section, Product information block,
 * cart page, cart drawer). The wrappers only supply configuration.
 *
 * The math at the top is deliberately DOM-free so it can be unit tested in Node.
 * See _working/tests/aef-bxgy-promotion.test.js.
 */
(function (global) {
  'use strict';

  // The cart drawer re-renders from templates/cart.ajax.liquid on every cart
  // change, and jQuery's replaceWith re-executes the <script> tag that comes
  // with it. Without this guard each drawer update would wrap fetch again and
  // reset the shared caches.
  if (global.AefBxgy) return;

  var PAGE_SIZE = 250;
  var MAX_PAGES = 8; // 2000 products. Beyond this we refuse to guess — see loadQualifyingProductIds.
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var CACHE_PREFIX = 'aef-bxgy:collection:';
  var REFRESH_DEBOUNCE_MS = 60;

  /* ------------------------------------------------------------------ *
   * Pure promotion math — no DOM, no network.
   * ------------------------------------------------------------------ */

  function isPositiveInteger(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value && value >= 1;
  }

  function isNonNegativeInteger(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value && value >= 0;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Validates merchant configuration. Returns an array of problems; empty means
   * the instance is safe to render. Collection overlap is checked separately,
   * once product ids are known, because it needs network data.
   */
  function validateConfig(config) {
    var problems = [];

    if (!isPositiveInteger(config.buyQuantity)) {
      problems.push('"Number needed to buy" must be a whole number of 1 or more.');
    }

    if (!isPositiveInteger(config.freeQuantity)) {
      problems.push('"Number received free" must be a whole number of 1 or more.');
    }

    if (!isNonNegativeInteger(config.maximumApplications)) {
      problems.push('"Maximum applications per order" must be 0 (unlimited) or a positive whole number.');
    }

    if (!config.buyCollection) {
      problems.push('Choose a collection for "Customer buys from".');
    }

    if (!config.sameCollection && !config.freeCollection) {
      problems.push('Choose a collection for "Customer gets from", or tick "Use the same collection for free items".');
    }

    return problems;
  }

  /**
   * Total cart quantity belonging to a set of product ids. Counts line-item
   * quantities, not lines or distinct products, so mixed products and variants
   * add up correctly.
   */
  function countCollectionUnits(cart, productIds) {
    if (!cart || !cart.items || !productIds) return 0;

    var lookup = productIds instanceof Set ? productIds : new Set(productIds);
    var total = 0;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (lookup.has(item.product_id)) {
        total += item.quantity;
      }
    }

    return total;
  }

  /**
   * Buy and free items come from the same collection, so one running count
   * drives a repeating cycle of (buy + free) units.
   */
  function getSameCollectionState(count, config) {
    var buyQuantity = config.buyQuantity;
    var freeQuantity = config.freeQuantity;
    var maximumApplications = config.maximumApplications;
    var hasMaximum = maximumApplications > 0;

    var cycleSize = buyQuantity + freeQuantity;
    var position = count % cycleSize;
    var completedOffers = Math.floor(count / cycleSize);

    // Past the maximum the customer keeps buying but stops earning, so the
    // "included" count has to stop climbing with the raw cycle count.
    var creditedOffers = hasMaximum ? Math.min(completedOffers, maximumApplications) : completedOffers;

    var result = {
      mode: 'same',
      count: count,
      cycleSize: cycleSize,
      position: position,
      completedOffers: completedOffers,
      freeIncluded: creditedOffers * freeQuantity,
      buyQuantity: buyQuantity,
      freeQuantity: freeQuantity,
      // Progress-indicator inputs, expressed as "units of the offer in flight".
      buyProgress: Math.min(position, buyQuantity),
      freeProgress: Math.max(position - buyQuantity, 0),
      remaining: 0
    };

    if (count === 0) {
      result.state = 'initial';
      return result;
    }

    if (hasMaximum && completedOffers >= maximumApplications) {
      result.state = 'maximum-reached';
      return result;
    }

    if (position === 0) {
      result.state = 'complete';
      // A completed offer reads as full, not as a reset to zero.
      result.buyProgress = buyQuantity;
      result.freeProgress = freeQuantity;
      return result;
    }

    if (position < buyQuantity) {
      result.state = completedOffers === 0 ? 'building' : 'building-repeat';
      result.remaining = clamp(buyQuantity - position, 1, buyQuantity);
      return result;
    }

    result.state = 'free-ready';
    result.remaining = clamp(cycleSize - position, 1, freeQuantity);
    return result;
  }

  /**
   * Buy and free items come from two different, non-overlapping collections, so
   * the two counts advance independently and an offer only completes when both
   * halves are present.
   */
  function getSeparateCollectionState(buyCount, freeCount, config) {
    var buyQuantity = config.buyQuantity;
    var freeQuantity = config.freeQuantity;
    var maximumApplications = config.maximumApplications;
    var hasMaximum = maximumApplications > 0;

    var rawEarnedOffers = Math.floor(buyCount / buyQuantity);
    var earnedOffers = hasMaximum ? Math.min(rawEarnedOffers, maximumApplications) : rawEarnedOffers;
    var rewardGroupsInCart = Math.floor(freeCount / freeQuantity);
    var completedOffers = Math.min(earnedOffers, rewardGroupsInCart);

    // Clamped so a cart holding reward products before any buy products — or
    // more rewards than earned — can never produce negative progress.
    var buyProgress = clamp(buyCount - completedOffers * buyQuantity, 0, buyQuantity);
    var freeProgress = clamp(freeCount - completedOffers * freeQuantity, 0, freeQuantity);

    var result = {
      mode: 'separate',
      buyCount: buyCount,
      freeCount: freeCount,
      cycleSize: buyQuantity + freeQuantity,
      completedOffers: completedOffers,
      // earnedOffers is already capped at the maximum, and completedOffers
      // cannot exceed it, so no further clamping is needed here.
      freeIncluded: completedOffers * freeQuantity,
      earnedOffers: earnedOffers,
      buyQuantity: buyQuantity,
      freeQuantity: freeQuantity,
      buyProgress: buyProgress,
      freeProgress: freeProgress,
      remaining: 0
    };

    if (buyCount === 0 && freeCount === 0) {
      result.state = 'initial';
      return result;
    }

    if (hasMaximum && completedOffers >= maximumApplications) {
      result.state = 'maximum-reached';
      return result;
    }

    if (earnedOffers > completedOffers) {
      result.state = 'free-ready';
      result.remaining = clamp(freeQuantity - freeProgress, 1, freeQuantity);
      return result;
    }

    if (completedOffers === 0) {
      result.state = 'building';
      result.remaining = clamp(buyQuantity - buyProgress, 1, buyQuantity);
      return result;
    }

    if (buyProgress === 0 && freeCount === completedOffers * freeQuantity) {
      result.state = 'complete';
      result.buyProgress = buyQuantity;
      result.freeProgress = freeQuantity;
      return result;
    }

    result.state = 'building-repeat';
    result.remaining = clamp(buyQuantity - buyProgress, 1, buyQuantity);
    return result;
  }

  /**
   * Visual palette for a state. Deliberately derived from semantic state rather
   * than a raw cart quantity, so it holds for any offer size and for both
   * counting modes.
   *
   *   1. free-ready            -> the next unit is the free one
   *   2. any free unit banked  -> a reward is already secured
   *   3. otherwise             -> still building the first offer
   *
   * For 3+1 that yields building, building, building, free-ready, earned,
   * earned, earned, free-ready, earned across quantities 0-8.
   */
  function getPalette(state) {
    if (state.state === 'free-ready') return 'free-ready';
    // maximum-reached always has a banked reward, but say so explicitly rather
    // than relying on that invariant holding for future offer shapes.
    if (state.state === 'maximum-reached') return 'earned';
    if (state.freeIncluded > 0) return 'earned';
    return 'building';
  }

  var MESSAGE_FOR_STATE = {
    'initial': 'initial',
    'building': 'building',
    'building-repeat': 'buildingRepeat',
    'free-ready': 'freeReady',
    'complete': 'complete',
    'maximum-reached': 'maximumReached'
  };

  /**
   * Whether the changing status message should render at all.
   *
   * At a completed offer the earned line already says a free unit is included,
   * and says it better because it carries the running count. Showing both is
   * redundant, so the earned line wins and the status message stands down.
   * Every other state pairs the two complementarily (e.g. "1 free included" +
   * "add 2 more for another"), so only `complete` is suppressed.
   */
  function shouldShowStatusMessage(state, config) {
    var template = config.messages[MESSAGE_FOR_STATE[state.state]];

    if (!template) return false; // merchant cleared the field

    var earnedLineShowing = Boolean(config.messages.earned) && state.freeIncluded > 0;

    return !(state.state === 'complete' && earnedLineShowing);
  }

  var TOKEN_PATTERN = /\{([a-zA-Z]+)\}/g;

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Replaces {tokens} in a merchant-authored message. Merchant inline-richtext
   * markup is preserved as markup; interpolated values are escaped. Unknown
   * tokens are left visible so a merchant sees the typo in the editor preview
   * rather than getting a silently broken sentence.
   */
  function interpolateMessage(template, values, onUnknownToken) {
    if (!template) return '';

    return String(template).replace(TOKEN_PATTERN, function (match, name) {
      if (Object.prototype.hasOwnProperty.call(values, name)) {
        return escapeHtml(values[name]);
      }

      if (typeof onUnknownToken === 'function') onUnknownToken(name);
      return match;
    });
  }

  function buildTokenValues(config, state) {
    return {
      numberneeded: config.buyQuantity,
      numberfree: config.freeQuantity,
      remaining: state.remaining,
      numberfreeincluded: state.freeIncluded,
      buyitemname: config.buyItemName,
      freeitemname: config.freeItemName
    };
  }

  function shouldShowLink(showLink, state) {
    if (showLink === 'always') return true;
    if (showLink === 'while-building') {
      return state === 'initial' || state === 'building' || state === 'building-repeat';
    }
    if (showLink === 'free-ready') return state === 'free-ready';
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Collection membership — cached and shared across every instance.
   * ------------------------------------------------------------------ */

  var collectionRequests = {}; // handle -> Promise<number[]>, also de-dupes in-flight requests

  function readCachedIds(handle) {
    try {
      var raw = global.sessionStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.ids)) return null;
      if (Date.now() - parsed.storedAt > CACHE_TTL_MS) return null;

      return parsed.ids;
    } catch (error) {
      return null;
    }
  }

  function writeCachedIds(handle, ids) {
    try {
      global.sessionStorage.setItem(
        CACHE_PREFIX + handle,
        JSON.stringify({ storedAt: Date.now(), ids: ids })
      );
    } catch (error) {
      // Private browsing or a full quota. Losing the cache is fine; we refetch.
    }
  }

  /**
   * Every published product id in a collection. Pages through the storefront
   * products endpoint rather than truncating at the first 250. A collection
   * larger than MAX_PAGES * PAGE_SIZE rejects, which hides the component — an
   * unknown-membership cart must never produce a confident progress message.
   */
  function loadQualifyingProductIds(handle) {
    if (!handle) return Promise.reject(new Error('A collection handle is required.'));
    if (collectionRequests[handle]) return collectionRequests[handle];

    var cached = readCachedIds(handle);
    if (cached) {
      collectionRequests[handle] = Promise.resolve(cached);
      return collectionRequests[handle];
    }

    var ids = [];

    function fetchPage(page) {
      if (page > MAX_PAGES) {
        throw new Error(
          'Collection "' + handle + '" holds more than ' + MAX_PAGES * PAGE_SIZE +
          ' products, which is more than the promotion component will page through.'
        );
      }

      var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_SIZE + '&page=' + page;

      return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Could not load collection "' + handle + '" (' + response.status + ').');
          }
          return response.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];

          for (var i = 0; i < products.length; i++) {
            ids.push(products[i].id);
          }

          if (products.length < PAGE_SIZE) return ids;
          return fetchPage(page + 1);
        });
    }

    collectionRequests[handle] = Promise.resolve()
      .then(function () { return fetchPage(1); })
      .then(function (allIds) {
        writeCachedIds(handle, allIds);
        return allIds;
      })
      .catch(function (error) {
        delete collectionRequests[handle]; // let a later refresh retry
        throw error;
      });

    return collectionRequests[handle];
  }

  /** identical | separate | overlapping — decides which counting mode applies. */
  function classifyCollections(buyHandle, freeHandle, buyIds, freeIds) {
    if (buyHandle === freeHandle) return 'identical';

    var freeSet = freeIds instanceof Set ? freeIds : new Set(freeIds);

    for (var i = 0; i < buyIds.length; i++) {
      if (freeSet.has(buyIds[i])) return 'overlapping';
    }

    return 'separate';
  }

  /* ------------------------------------------------------------------ *
   * Cart state — fetched once per refresh cycle, shared by every instance.
   * ------------------------------------------------------------------ */

  var cartRequest = null;

  function getCart(forceRefresh) {
    if (cartRequest && !forceRefresh) return cartRequest;

    cartRequest = fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not load the cart (' + response.status + ').');
        return response.json();
      })
      .catch(function (error) {
        cartRequest = null; // don't cache a failure
        throw error;
      });

    return cartRequest;
  }

  /* ------------------------------------------------------------------ *
   * Instance registry.
   * ------------------------------------------------------------------ */

  var instances = new Set();
  var refreshTimer = null;

  /**
   * Re-renders every mounted instance. Pass a cart to skip the network round
   * trip; otherwise one shared /cart.js request serves all of them.
   */
  function refreshPromotionInstances(cart) {
    var pending = cart ? Promise.resolve(cart) : getCart(true);

    return pending
      .then(function (freshCart) {
        instances.forEach(function (instance) { instance.applyCart(freshCart); });
        return freshCart;
      })
      .catch(function (error) {
        instances.forEach(function (instance) { instance.handleFailure(error); });
      });
  }

  function scheduleRefresh() {
    if (refreshTimer) global.clearTimeout(refreshTimer);
    refreshTimer = global.setTimeout(function () {
      refreshTimer = null;
      refreshPromotionInstances();
    }, REFRESH_DEBOUNCE_MS);
  }

  /**
   * Only one visible instance narrates cart changes, so a page component and an
   * off-screen cart drawer can't announce the same message twice.
   */
  function updateLiveRegionOwnership() {
    var visible = [];

    instances.forEach(function (instance) {
      if (instance.isVisible()) visible.push(instance);
    });

    visible.sort(function (a, b) {
      var position = a.compareDocumentPosition(b);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    instances.forEach(function (instance) {
      instance.setAnnounces(instance === visible[0]);
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart observation.
   *
   * Flex has no cart events of its own: z__jsAjaxCart.js and z__jsCart.js mutate
   * the cart with jQuery and call updateView() directly. So we watch the traffic
   * instead of the theme's internals — jQuery's global ajaxComplete covers every
   * PXU mutation, a fetch wrapper covers the newer AEF bulk-order code, and
   * bulk:cart-updated covers the bulk-order steppers that already broadcast.
   * ------------------------------------------------------------------ */

  var CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function isCartMutationUrl(url) {
    return typeof url === 'string' && CART_MUTATION_PATTERN.test(url);
  }

  var observationStarted = false;

  function startCartObservation() {
    if (observationStarted) return;
    observationStarted = true;

    if (global.jQuery) {
      global.jQuery(document).on('ajaxComplete', function (event, xhr, settings) {
        if (settings && isCartMutationUrl(settings.url)) scheduleRefresh();
      });
    }

    if (typeof global.fetch === 'function') {
      var nativeFetch = global.fetch;

      global.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url);
        var result = nativeFetch.apply(this, arguments);

        if (isCartMutationUrl(url)) {
          result.then(function () { scheduleRefresh(); }, function () {});
        }

        return result;
      };
    }

    document.addEventListener('bulk:cart-updated', function (event) {
      if (event.detail && event.detail.cart) {
        cartRequest = Promise.resolve(event.detail.cart);
        refreshPromotionInstances(event.detail.cart);
      } else {
        scheduleRefresh();
      }
    });

    // Opening the drawer re-renders it from templates/cart.ajax.liquid; make
    // sure what it shows reflects the current cart, not a stale first paint.
    document.addEventListener('click', function (event) {
      if (event.target && event.target.closest && event.target.closest('[data-ajax-cart-trigger]')) {
        scheduleRefresh();
      }
    });

    document.addEventListener('shopify:section:load', function () { scheduleRefresh(); });
    document.addEventListener('shopify:section:unload', function () { updateLiveRegionOwnership(); });
  }

  /* ------------------------------------------------------------------ *
   * Custom element.
   * ------------------------------------------------------------------ */

  function ConfigurationError(message) {
    this.name = 'ConfigurationError';
    this.message = message;
  }
  ConfigurationError.prototype = Object.create(Error.prototype);
  ConfigurationError.prototype.constructor = ConfigurationError;

  if (global.customElements && !global.customElements.get('aef-bxgy-promotion')) {
    class AefBxgyPromotion extends HTMLElement {
      connectedCallback() {
        this.config = this.readConfig();
        this.messageEl = this.querySelector('[data-bxgy-message]');
        this.earnedEl = this.querySelector('[data-bxgy-earned]');
        // The region wraps both status lines; fall back to the message element
        // so an older cached snippet still announces something.
        this.liveEl = this.querySelector('[data-bxgy-live]') || this.messageEl;
        this.progressEl = this.querySelector('[data-bxgy-progress]');
        this.linkEl = this.querySelector('[data-bxgy-link]');
        this.noticeEl = this.querySelector('[data-bxgy-notice]');
        this.lastMessageHtml = null;
        this.lastEarnedHtml = null;
        this.hasVerifiedState = false;
        this.announces = false;

        if (!this.config) {
          this.fail('This promotion block could not read its settings.');
          return;
        }

        var problems = validateConfig(this.config);

        if (problems.length) {
          this.fail(problems.join(' '));
          return;
        }

        startCartObservation();
        instances.add(this);

        var self = this;

        // Mount uses the *shared* cart promise rather than forcing a refresh, so
        // four instances on one page still make one /cart.js request between them.
        this.loadEligibility()
          .then(function () { return getCart(false); })
          .then(function (cart) { self.applyCart(cart); })
          .catch(function (error) { self.handleFailure(error); });
      }

      disconnectedCallback() {
        instances.delete(this);
        updateLiveRegionOwnership();
      }

      readConfig() {
        // Carried on an HTML attribute rather than an inline JSON <script> so
        // merchant message text can never terminate the tag it lives in.
        var raw = this.getAttribute('data-config');
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch (error) {
          return null;
        }
      }

      /**
       * Resolves collection membership and settles which counting mode applies.
       * The same handle on both sides loads the collection only once.
       */
      loadEligibility() {
        var config = this.config;
        var buyHandle = config.buyCollection;
        var freeHandle = config.sameCollection ? buyHandle : config.freeCollection;
        var self = this;

        return loadQualifyingProductIds(buyHandle).then(function (buyIds) {
          if (freeHandle === buyHandle) {
            self.buyIds = new Set(buyIds);
            self.freeIds = self.buyIds;
            self.countingMode = 'same';
            return;
          }

          return loadQualifyingProductIds(freeHandle).then(function (freeIds) {
            var relationship = classifyCollections(buyHandle, freeHandle, buyIds, freeIds);

            if (relationship === 'overlapping') {
              // Ambiguous attribution: the same unit could count as bought or as free.
              throw new ConfigurationError(
                'The "Customer buys from" and "Customer gets from" collections share products. ' +
                'Use one collection for both, or two collections with no products in common.'
              );
            }

            self.buyIds = new Set(buyIds);
            self.freeIds = new Set(freeIds);
            self.countingMode = relationship === 'identical' ? 'same' : 'separate';
          });
        });
      }

      applyCart(cart) {
        if (!this.buyIds) return; // eligibility not resolved yet

        var state;

        if (this.countingMode === 'same') {
          state = getSameCollectionState(countCollectionUnits(cart, this.buyIds), this.config);
        } else {
          state = getSeparateCollectionState(
            countCollectionUnits(cart, this.buyIds),
            countCollectionUnits(cart, this.freeIds),
            this.config
          );
        }

        this.hasVerifiedState = true;
        this.render(state);
      }

      /**
       * A failed collection or cart load must never leave a confident but wrong
       * message on screen: keep the last verified state, or show nothing.
       */
      handleFailure(error) {
        if (error instanceof ConfigurationError) {
          this.fail(error.message);
          return;
        }

        if (!this.hasVerifiedState) {
          this.hidden = true;
          updateLiveRegionOwnership();
        }

        if (global.Shopify && global.Shopify.designMode) {
          console.warn('[aef-bxgy-promotion]', (error && error.message) || error);
        }
      }

      /** Merchant-facing problem: hide on the storefront, explain in the editor. */
      fail(message) {
        this.hidden = true;
        updateLiveRegionOwnership();

        if (global.Shopify && global.Shopify.designMode) {
          console.warn('[aef-bxgy-promotion] ' + message);

          if (this.noticeEl) {
            this.noticeEl.textContent = message;
            this.noticeEl.hidden = false;
            this.hidden = false;
            this.setAttribute('data-state', 'invalid');
          }
        }
      }

      render(state) {
        var config = this.config;

        if (state.state === 'complete' && !config.showAtComplete) {
          this.hidden = true;
          updateLiveRegionOwnership();
          return;
        }

        var unknownTokens = [];
        var showMessage = shouldShowStatusMessage(state, config);
        var template = showMessage ? config.messages[MESSAGE_FOR_STATE[state.state]] : '';
        var html = interpolateMessage(template, buildTokenValues(config, state), function (name) {
          unknownTokens.push(name);
        });

        if (unknownTokens.length && global.Shopify && global.Shopify.designMode) {
          console.warn(
            '[aef-bxgy-promotion] Unrecognized message token(s): {' + unknownTokens.join('}, {') + '}. ' +
            'Supported tokens are {numberneeded}, {numberfree}, {remaining}, {numberfreeincluded}, '  +
            '{buyitemname} and {freeitemname}.'
          );
        }

        this.hidden = false;
        this.setAttribute('data-state', state.state);
        this.setAttribute('data-palette', getPalette(state));

        if (this.messageEl) {
          if (html !== this.lastMessageHtml) {
            this.messageEl.innerHTML = html;
            this.lastMessageHtml = html;
          }
          // hidden rather than empty, so the flex gap collapses with it.
          this.messageEl.hidden = !showMessage;
        }

        this.renderEarned(state, unknownTokens);
        this.renderProgress(state);
        this.renderLink(state);
        updateLiveRegionOwnership();
      }

      /**
       * Confirmation line above the status message, e.g. "1 free Dye Pack
       * included". Shown only once at least one free unit is actually in the
       * cart, so it never promises a reward the customer has not got yet.
       */
      renderEarned(state, unknownTokens) {
        if (!this.earnedEl) return;

        var template = this.config.messages.earned;

        if (!template || !state.freeIncluded) {
          this.earnedEl.hidden = true;
          return;
        }

        var html = interpolateMessage(template, buildTokenValues(this.config, state), function (name) {
          unknownTokens.push(name);
        });

        if (html !== this.lastEarnedHtml) {
          this.earnedEl.innerHTML = html;
          this.lastEarnedHtml = html;
        }

        this.earnedEl.hidden = false;
      }

      renderProgress(state) {
        if (!this.progressEl) return;

        var segments = this.progressEl.querySelectorAll('[data-bxgy-segment]');

        for (var i = 0; i < segments.length; i++) {
          var segment = segments[i];
          var index = Number(segment.getAttribute('data-bxgy-segment-index'));
          var isReward = segment.getAttribute('data-bxgy-segment-kind') === 'reward';
          var filled = isReward ? index < state.freeProgress : index < state.buyProgress;

          segment.classList.toggle('is-filled', filled);
        }
      }

      renderLink(state) {
        if (!this.linkEl) return;
        this.linkEl.hidden = !shouldShowLink(this.config.link.show, state.state);
      }

      isVisible() {
        if (this.hidden) return false;
        // getClientRects is empty for display:none and for any display:none
        // ancestor, and unlike offsetParent it is correct for the fixed-position
        // cart drawer.
        return this.getClientRects().length > 0;
      }

      setAnnounces(shouldAnnounce) {
        if (!this.liveEl || this.announces === shouldAnnounce) return;

        this.announces = shouldAnnounce;
        this.liveEl.setAttribute('aria-live', shouldAnnounce ? 'polite' : 'off');
      }
    }

    global.customElements.define('aef-bxgy-promotion', AefBxgyPromotion);
  }

  global.AefBxgy = {
    countCollectionUnits: countCollectionUnits,
    getSameCollectionState: getSameCollectionState,
    getSeparateCollectionState: getSeparateCollectionState,
    interpolateMessage: interpolateMessage,
    getPalette: getPalette,
    shouldShowStatusMessage: shouldShowStatusMessage,
    validateConfig: validateConfig,
    classifyCollections: classifyCollections,
    shouldShowLink: shouldShowLink,
    escapeHtml: escapeHtml,
    loadQualifyingProductIds: loadQualifyingProductIds,
    refreshPromotionInstances: refreshPromotionInstances,
    ConfigurationError: ConfigurationError
  };
})(typeof window !== 'undefined' ? window : this);
